import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import API_URL from '../config';
import SharedNav from './SharedNav';
import './OnboardingInformation.css';

const emptyStation = () => ({
  stationNumber: '',
  stationAddress: '',
  csaCoverage: '', routeTypes: '', driverTypes: '', monthlyHires: '',
  fleetDetails: '', technology: '', indeedLink: '', indeedAdminEmail: '',
  prescreeningQuestions: '',
  fadvAccount: '', fadvClientId: '', fadvUsername: '', fadvPassword: '',
  fadvSecretAnswer: '',
  scheduleArea: '', reportTime: '', dispatchTime: '', stemTimesTo: '',
  stemTimesFrom: '', onRoadHours: '', typicalHours: '', typicalMiles: '',
  routeMiles: '', operatingDays: '', newHireSchedule: '', daysOff: '',
  consecutiveDaysOff: '', additionalScheduleInfo: '', payPeriod: '',
  payStructure: '', trainingPay: '', regularPay: '', topDriverPay: '',
  raiseSchedule: '', trainingDuration: '', overtimeAvailable: '',
  overtimeRate: '', stopBonus: false, stopBonusDetails: '',
  performanceBonus: false, safetyBonus: false,
  attendanceBonus: false, vacationSickTime: '', ipads: false, medical: false,
  dental: false, vision: false, retirement401k: false, retirement401kMatching: false,
  otherBenefits: '', routingApp: '', promotionCriteria: '',
  drivingDays: '', consecutiveDaysOff2: '', additionalInfo: '',
  lhAssigned: false, lhUnassigned: false, lhAssignedLanes: '',
  lhSoloRun: false, lhTeamRun: false, lhBothRun: false,
  lhDispatchTime: '', lhWeeklySchedule: '',
  lhEndorsementNone: false, lhEndorsementDoubles: false,
  lhEndorsementTriples: false, lhEndorsementHazmats: false,
  lhEndorsementOther: false, lhEndorsementOtherText: '',
  lhTruckInfo: '', lhPayPerMile: false, lhFlatDailyWage: false,
  lhHourly: false, lhPayOther: false, lhPayOtherText: '',
  lhPayPerMileAmount: '', lhPayPeriod: '', lhIncentives: '',
  lhVacationSickTime: '', lhBenefits: '', lhAdditionalInfo: ''
});

// Migrate old flat-field format to new stations-array format
const migrateFormData = (data) => {
  if (!data) return data;
  if (data.stations && Array.isArray(data.stations)) return data;
  const s = emptyStation();
  const fields = Object.keys(s);
  fields.forEach(f => { if (data[f] !== undefined) s[f] = data[f]; });
  s.stationAddress = data.terminalAddress || '';
  return {
    entityName: data.entityName || '',
    billingContactName: data.billingContactName || '',
    billingContactEmail: data.billingContactEmail || '',
    billingContactPhone: data.billingContactPhone || '',
    billingStreet: data.billingStreet || '',
    billingCity: data.billingCity || '',
    billingState: data.billingState || '',
    billingZip: data.billingZip || '',
    entityId: data.entityId || '',
    sameContact: data.sameContact || '',
    recruitingContactName: data.recruitingContactName || '',
    recruitingContactPhone: data.recruitingContactPhone || '',
    serviceType: data.serviceType || '',
    stationCount: 1,
    stations: [s]
  };
};

const emptyFormData = () => ({
  entityName: '', billingContactName: '', billingContactEmail: '',
  billingContactPhone: '', billingStreet: '', billingCity: '',
  billingState: '', billingZip: '', entityId: '',
  sameContact: '', recruitingContactName: '', recruitingContactPhone: '',
  serviceType: '', stationCount: 1, stations: [emptyStation()]
});

// Pulls a short "who's login is this" value out of free-text recruiter notes, for the
// Submissions list table. Notes are free-form (recruiters paste FADV credentials, protocol
// essays, whatever) so this is a best-effort extraction, not a guaranteed-correct parse:
// - Primary: any line with a "User ID"/"Username"/"User Name" label (any casing, colon/dash/
//   space separator) -- e.g. "User ID: PNorris", "Username        amiller". Multiple matches
//   (multi-station accounts) are deduped and joined with a comma.
// - Fallback: notes with NO label at all, just stacked raw lines (Client ID / Username /
//   Password / Secret Answer with nothing labeling them) -- second non-empty line is assumed
//   to be the username, matching that unlabeled convention. This fallback can't tell a real
//   unlabeled credential block from unrelated free-text notes, so it will occasionally surface
//   a non-username value for notes that aren't a credentials block at all.
function extractRecruiterNotesUserId(notes) {
  if (!notes || !notes.trim()) return '';
  const labelRegex = /user\s*(?:id|name)\s*[:-]?\s*(.+)/gi;
  const matches = [];
  let m;
  while ((m = labelRegex.exec(notes)) !== null) {
    const val = m[1].trim();
    if (val) matches.push(val);
  }
  if (matches.length > 0) {
    return [...new Set(matches)].join(', ');
  }
  const lines = notes.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  return lines[1] || lines[0] || '';
}

function OnboardingInformation({ user, onLogout, hideHeader = false }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submissions, setSubmissions] = useState([]);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'detail'
  const [notesModalSubmission, setNotesModalSubmission] = useState(null); // submissions-list row whose full recruiter notes are open in the modal
  const [saveStatus, setSaveStatus] = useState(''); // '', 'saving', 'saved', 'error'
  const [signature, setSignature] = useState({
    name: '',
    company: '',
    timestamp: null,
    isSigned: false
  });
  const [companies, setCompanies] = useState([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState(null);
  const [viewingUserId, setViewingUserId] = useState(null);
  const isInitialLoad = React.useRef(true);
  const [recruiterNotes, setRecruiterNotes] = useState('');
  const [recruiterNotesOpen, setRecruiterNotesOpen] = useState(false);
  const [recruiterNotesSaving, setRecruiterNotesSaving] = useState(false);
  const [recruiterNotesSaved, setRecruiterNotesSaved] = useState(false);

  const [activeStation, setActiveStation] = useState(0);

  const [formData, setFormData] = useState({
    // Billing Info (flat — same for all stations)
    entityName: '',
    billingContactName: '',
    billingContactEmail: '',
    billingContactPhone: '',
    billingStreet: '',
    billingCity: '',
    billingState: '',
    billingZip: '',
    entityId: '',
    sameContact: '',
    recruitingContactName: '',
    recruitingContactPhone: '',

    // Service type (whole company)
    serviceType: '',

    // Per-station data
    stationCount: 1,
    stations: [emptyStation()]
  });

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Load company-specific onboarding data when selectedCompanyId changes
  useEffect(() => {
    if (selectedCompanyId && user.role?.toLowerCase() === 'customer') {
      isInitialLoad.current = true; // reset so next setFormData is treated as initial load
      loadCompanyOnboardingData(selectedCompanyId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyId]);

  const loadCompanyOnboardingData = async (companyId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API_URL}/api/onboarding/${user.id}?companyId=${companyId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data && response.data.data) {
        setFormData(migrateFormData(response.data.data));
        setSignature(response.data.signature || {
          name: '',
          company: '',
          timestamp: null,
          isSigned: false
        });
        setRecruiterNotes(response.data.recruiter_notes || '');
        setActiveStation(0);
      } else {
        // Reset form for new company
        setFormData(emptyFormData());
        setSignature({ name: '', company: '', timestamp: null, isSigned: false });
        setActiveStation(0);
      }
    } catch (err) {
      console.error('Error loading company onboarding data:', err);
    }
  };


  // Autosave effect - saves 15 seconds after user stops typing
  useEffect(() => {
    if (user.role?.toLowerCase() !== 'customer') return; // Only autosave for customers

    // Skip autosave on initial data load
    if (isInitialLoad.current) {
      isInitialLoad.current = false;
      return;
    }

    const autoSaveTimer = setTimeout(() => {
      // Only autosave if there's some data filled in
      if (formData.entityName || formData.billingContactName) {
        handleAutoSave();
      }
    }, 15000); // 15 second delay after last change

    return () => clearTimeout(autoSaveTimer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData]);

  const handleAutoSave = async () => {
    if (!selectedCompanyId) return; // Don't save if no company selected

    try {
      setSaveStatus('saving');
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/onboarding`,
        { ...formData, userId: user.id, company_id: selectedCompanyId, signature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
      console.log('Form auto-saved');
    } catch (err) {
      console.error('Auto-save failed:', err);
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
    }
  };

  const handleSaveNow = async (isExplicitSubmit = false) => {
    if (!selectedCompanyId) {
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
      return { success: false, reason: 'no_company' };
    }

    try {
      setSaveStatus('saving');
      const token = localStorage.getItem('token');
      await axios.post(
        `${API_URL}/api/onboarding`,
        { ...formData, userId: user.id, company_id: selectedCompanyId, signature, isExplicitSubmit },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSaveStatus('saved');
      setTimeout(() => setSaveStatus(''), 2000);
      return { success: true };
    } catch (err) {
      console.error('Save failed:', err);
      const serverMsg = err.response?.data?.error;
      const status = err.response?.status;
      setSaveStatus('error');
      setTimeout(() => setSaveStatus(''), 3000);
      return { success: false, reason: 'api_error', status, serverMsg };
    }
  };

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');

      if (user.role?.toLowerCase() === 'customer') {
        // Fetch user's companies
        const companiesResponse = await axios.get(`${API_URL}/api/users/my-companies`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompanies(companiesResponse.data);

        // Set default to first company if available
        if (companiesResponse.data.length > 0 && !selectedCompanyId) {
          setSelectedCompanyId(companiesResponse.data[0].id);
        }
      } else {
        // Recruiter/Admin loads all submissions
        const response = await axios.get(
          `${API_URL}/api/onboarding`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        setSubmissions(response.data);
      }
      setLoading(false);
    } catch (err) {
      console.error('Error loading onboarding data:', err);
      setLoading(false);
    }
  };

  const loadCustomerData = async (userId, companyId) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const url = companyId
        ? `${API_URL}/api/onboarding/${userId}?companyId=${companyId}`
        : `${API_URL}/api/onboarding/${userId}`;
      const response = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      // With companyId returns single object; without returns array — take first
      const record = Array.isArray(response.data) ? response.data[0] : response.data;
      if (record?.data) setFormData(migrateFormData(record.data));
      if (record?.signature) {
        setSignature(record.signature);
      } else {
        setSignature({ name: '', company: '', timestamp: null, isSigned: false });
      }
      setRecruiterNotes(record?.recruiter_notes || '');
      setActiveStation(0);
    } catch (err) {
      console.error('Error loading customer data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Update a field on the currently-active station
  const handleStationChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => {
      const stations = [...(prev.stations || [])];
      stations[activeStation] = { ...(stations[activeStation] || emptyStation()), [name]: type === 'checkbox' ? checked : value };
      return { ...prev, stations };
    });
  };

  // Update a specific field on a specific station index (used by address/number inputs)
  const handleStationFieldChange = (idx, field, value) => {
    setFormData(prev => {
      const stations = [...(prev.stations || [])];
      while (stations.length <= idx) stations.push(emptyStation());
      stations[idx] = { ...stations[idx], [field]: value };
      return { ...prev, stations };
    });
  };

  // Set station count, growing or shrinking the stations array
  const handleStationCount = (n) => {
    if (n < 1 || n > 6) return;
    setFormData(prev => {
      const stations = [...(prev.stations || [])];
      while (stations.length < n) stations.push(emptyStation());
      return { ...prev, stationCount: n, stations: stations.slice(0, n) };
    });
    if (activeStation >= n) setActiveStation(n - 1);
  };

  const handleSignatureChange = (e) => {
    const { name, value } = e.target;
    setSignature(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSignDocument = () => {
    // Validate both fields are filled
    if (!signature.name.trim() || signature.name.trim().length < 2) {
      alert('Please enter your full name (at least 2 characters)');
      return;
    }
    if (!signature.company.trim() || signature.company.trim().length < 2) {
      alert('Please enter your company name (at least 2 characters)');
      return;
    }

    // Mark as signed
    setSignature(prev => ({
      ...prev,
      isSigned: true,
      timestamp: new Date().toISOString()
    }));

    // Save immediately after signing
    handleSaveNow();
  };

  const handleClearSignature = async () => {
    if (!window.confirm('Clear this signature? The client will need to re-sign the document.')) return;
    const clearedSignature = { name: '', company: '', timestamp: null, isSigned: false };
    setSignature(clearedSignature);
    try {
      const token = localStorage.getItem('token');
      const targetUserId = viewingUserId || user.id;
      await axios.post(
        `${API_URL}/api/onboarding`,
        { ...formData, userId: targetUserId, company_id: selectedCompanyId, signature: clearedSignature },
        { headers: { Authorization: `Bearer ${token}` } }
      );
    } catch (err) {
      console.error('Failed to clear signature:', err);
    }
  };

  const handleSaveRecruiterNotes = async () => {
    setRecruiterNotesSaving(true);
    setRecruiterNotesSaved(false);
    try {
      const token = localStorage.getItem('token');
      const targetUserId = viewingUserId || user.id;
      await axios.post(
        `${API_URL}/api/onboarding`,
        { ...formData, userId: targetUserId, company_id: selectedCompanyId, signature, recruiter_notes: recruiterNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRecruiterNotesSaved(true);
      setTimeout(() => setRecruiterNotesSaved(false), 2500);
    } catch (err) {
      console.error('Failed to save recruiter notes:', err);
    } finally {
      setRecruiterNotesSaving(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate signature for customers
    if (user.role?.toLowerCase() === 'customer' && !signature.isSigned) {
      alert("Your information has already been saved — no need to worry about losing it. To complete your submission, please sign the document above first.");
      return;
    }

    const result = await handleSaveNow(true);
    if (result.success) {
      alert('Onboarding information saved successfully!');
    } else if (result.reason === 'no_company') {
      alert('Your account is not linked to a company yet. Please contact your Mountain Recruiting representative to complete your account setup.');
    } else if (result.status === 401) {
      alert('Your session has expired. Please save a copy of your work, then log in again.');
    } else {
      alert(`Error saving onboarding information. ${result.serverMsg ? result.serverMsg + '.' : 'Please try again or contact support.'}`);
    }
  };

  // Recruiter/Admin: Show list of all submissions
  if (user.role?.toLowerCase() !== 'customer' && viewMode === 'list') {
    return (
      <div className="onboarding-container">
        {!hideHeader && user?.role?.toLowerCase() !== 'customer' && (
          <SharedNav user={user} onLogout={onLogout} />
        )}

        <div className="onboarding-content onboarding-content--submissions-list">
          {loading ? (
            <div className="loading-state">Loading submissions...</div>
          ) : (
            <>
              <h2>Customer Onboarding Submissions</h2>
              {submissions.length === 0 ? (
                <div className="empty-state">No onboarding submissions yet.</div>
              ) : (
                <div className="submissions-table-wrap">
                  <table className="submissions-table">
                    <thead>
                      <tr>
                        <th>Email</th>
                        <th>Submitted</th>
                        <th>Edited</th>
                        <th>Entity Name</th>
                        <th>Station</th>
                        <th>Client ID</th>
                        <th>Recruiter Notes</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissions.map(submission => {
                        const migrated = migrateFormData(submission.data) || {};
                        const stationsArr = migrated.stations || [];
                        const stationNumbers = stationsArr.map(s => s.stationNumber).filter(Boolean).join(', ');
                        const clientIds = stationsArr.map(s => s.fadvClientId).filter(Boolean).join(', ');
                        const notesUserId = extractRecruiterNotesUserId(submission.recruiter_notes);
                        return (
                          <tr key={submission.user_id}>
                            <td>{submission.user_email}</td>
                            <td>{submission.created_at ? new Date(submission.created_at).toLocaleDateString() : 'N/A'}</td>
                            <td>{submission.updated_at ? new Date(submission.updated_at).toLocaleDateString() : 'N/A'}</td>
                            <td>{submission.data.entityName || 'N/A'}</td>
                            <td>{stationNumbers || 'N/A'}</td>
                            <td>{clientIds || 'N/A'}</td>
                            <td>
                              <div className="submissions-notes-cell">
                                <span className="submissions-notes-value">{notesUserId || '—'}</span>
                                {submission.recruiter_notes && submission.recruiter_notes.trim() && (
                                  <button
                                    type="button"
                                    className="submissions-notes-btn"
                                    title="View full recruiter notes"
                                    onClick={() => setNotesModalSubmission(submission)}
                                  >
                                    💬
                                  </button>
                                )}
                              </div>
                            </td>
                            <td>
                              <button
                                onClick={() => {
                                  setSubmissions([]);
                                  setViewingUserId(submission.user_id);
                                  setSelectedCompanyId(submission.company_id);
                                  setViewMode('detail');
                                  loadCustomerData(submission.user_id, submission.company_id);
                                }}
                                className="submissions-view-btn"
                              >
                                View Details
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </>
          )}
        </div>
        {notesModalSubmission && (
          <div className="submissions-notes-modal-overlay" onClick={() => setNotesModalSubmission(null)}>
            <div className="submissions-notes-modal" onClick={e => e.stopPropagation()}>
              <div className="submissions-notes-modal-header">
                <h3>Recruiter Notes: {notesModalSubmission.data?.entityName || notesModalSubmission.user_email}</h3>
                <button type="button" className="submissions-notes-modal-close" onClick={() => setNotesModalSubmission(null)}>✕</button>
              </div>
              <pre className="submissions-notes-modal-body">{notesModalSubmission.recruiter_notes}</pre>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Customer view or Recruiter viewing details
  const isRecruiterViewing = user.role?.toLowerCase() !== 'customer' && viewMode === 'detail';
  const isReadOnly = isRecruiterViewing;

  return (
    <div className="onboarding-container">
      {!hideHeader && user?.role?.toLowerCase() !== 'customer' && (
        <SharedNav user={user} onLogout={onLogout} />
      )}

      <div className="onboarding-content">
        {loading ? (
          <div className="loading-state">Loading...</div>
        ) : (
          <>
            {user.role?.toLowerCase() === 'customer' && companies.length === 0 && (
              <div className="read-only-banner" style={{background:'#fff3cd',borderColor:'#ffc107',color:'#856404',display:'flex',alignItems:'center',justifyContent:'space-between',gap:'16px'}}>
                <span><strong>⚠️ Account setup incomplete.</strong> Your account is not linked to a company yet. If your representative recently added you, try refreshing.</span>
                <button
                  type="button"
                  onClick={() => { loadData(); }}
                  style={{background:'#856404',color:'#fff',border:'none',borderRadius:'6px',padding:'6px 14px',cursor:'pointer',fontWeight:'600',whiteSpace:'nowrap'}}
                >
                  ↻ Retry
                </button>
              </div>
            )}

            {isRecruiterViewing && (
              <button
                type="button"
                onClick={() => { setViewMode('list'); loadData(); }}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'none',
                  border: '1px solid #cbd5e1',
                  borderRadius: '6px',
                  padding: '7px 14px',
                  fontSize: '13px',
                  fontWeight: '600',
                  color: '#1e2d5a',
                  cursor: 'pointer',
                  marginBottom: '12px',
                }}
              >
                ← Back to All Submissions
              </button>
            )}

            {isReadOnly && (
              <div className="read-only-banner">
                <strong>Viewing Mode:</strong> You are viewing this customer's onboarding information in read-only mode.
              </div>
            )}

            {/* Company Tabs for Multi-Company Customers */}
            {user.role?.toLowerCase() === 'customer' && companies.length > 1 && (
              <div className="company-tabs" style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '20px',
                borderBottom: '2px solid #dee2e6'
              }}>
                {companies.map(company => (
                  <button
                    key={company.id}
                    type="button"
                    className={`tab ${selectedCompanyId === company.id ? 'active' : ''}`}
                    onClick={() => setSelectedCompanyId(company.id)}
                    style={{
                      padding: '12px 24px',
                      border: 'none',
                      background: 'none',
                      cursor: 'pointer',
                      fontSize: '15px',
                      fontWeight: '500',
                      color: selectedCompanyId === company.id ? '#007bff' : '#666',
                      borderBottom: selectedCompanyId === company.id ? '3px solid #007bff' : '3px solid transparent',
                      transition: 'all 0.2s'
                    }}
                  >
                    {company.name}
                  </button>
                ))}
              </div>
            )}

            {!isReadOnly && saveStatus && (
              <div style={{
                padding: '10px 20px',
                marginBottom: '20px',
                borderRadius: '4px',
                textAlign: 'center',
                backgroundColor: saveStatus === 'saved' ? '#d4edda' : saveStatus === 'saving' ? '#fff3cd' : '#f8d7da',
                color: saveStatus === 'saved' ? '#155724' : saveStatus === 'saving' ? '#856404' : '#721c24',
                border: `1px solid ${saveStatus === 'saved' ? '#c3e6cb' : saveStatus === 'saving' ? '#ffeeba' : '#f5c6cb'}`
              }}>
                {saveStatus === 'saving' && '💾 Saving...'}
                {saveStatus === 'saved' && '✓ Saved successfully'}
                {saveStatus === 'error' && '✗ Error saving. Please try again.'}
              </div>
            )}

            <form onSubmit={handleSubmit} className="onboarding-form">

          {/* Recruiter Notes — always visible to admin/recruiter; customers only see it after signing */}
          {(signature.isSigned || user.role?.toLowerCase() !== 'customer') && (
            <div className="recruiter-notes-section">
              <button
                type="button"
                className="recruiter-notes-toggle"
                onClick={() => setRecruiterNotesOpen(v => !v)}
              >
                <span className="recruiter-notes-label">📋 Recruiter Notes</span>
                <span className="recruiter-notes-chevron">{recruiterNotesOpen ? '▲' : '▼'}</span>
              </button>
              {recruiterNotesOpen && (
                <div className="recruiter-notes-body">
                  {user.role?.toLowerCase() === 'customer' ? (
                    <p className="recruiter-notes-readonly">
                      {recruiterNotes || <em style={{ color: '#9ca3af' }}>No notes yet.</em>}
                    </p>
                  ) : (
                    <>
                      <textarea
                        className="recruiter-notes-textarea"
                        value={recruiterNotes}
                        onChange={e => setRecruiterNotes(e.target.value)}
                        placeholder="Add internal notes about this customer's onboarding…"
                        rows={4}
                      />
                      <div className="recruiter-notes-footer">
                        <button
                          type="button"
                          className="recruiter-notes-save"
                          onClick={handleSaveRecruiterNotes}
                          disabled={recruiterNotesSaving}
                        >
                          {recruiterNotesSaving ? 'Saving…' : recruiterNotesSaved ? '✓ Saved' : 'Save Notes'}
                        </button>
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          )}

          {/* Station Setup Section */}
          <div className="form-section station-setup-section">
            <h2>Stations</h2>
            <p className="station-setup-desc">
              How many stations would you like to start recruiting for?
            </p>

            {/* Count selector — hidden after signing (use Add button instead) */}
            {(!signature.isSigned || isReadOnly) && (
              <div className="station-count-row">
                {[1,2,3,4,5,6].map(n => (
                  <button
                    key={n}
                    type="button"
                    className={`station-count-btn${formData.stationCount === n ? ' active' : ''}`}
                    onClick={() => handleStationCount(n)}
                    disabled={isReadOnly}
                  >
                    {n}
                  </button>
                ))}
              </div>
            )}

            {/* Per-station address + number */}
            <div className="station-address-list">
              {Array.from({ length: formData.stationCount }).map((_, i) => (
                <div key={i} className="station-address-row">
                  <div className="form-group" style={{ flex: 3 }}>
                    <label>Station {i + 1} Address</label>
                    <input
                      type="text"
                      value={formData.stations?.[i]?.stationAddress || ''}
                      onChange={e => handleStationFieldChange(i, 'stationAddress', e.target.value)}
                      placeholder="e.g. 123 Terminal Blvd, Phoenix, AZ 85001"
                      disabled={isReadOnly}
                    />
                  </div>
                  <div className="form-group" style={{ flex: 1 }}>
                    <label>Station {i + 1} Number</label>
                    <input
                      type="text"
                      value={formData.stations?.[i]?.stationNumber || ''}
                      onChange={e => handleStationFieldChange(i, 'stationNumber', e.target.value)}
                      placeholder="e.g. PHXA"
                      disabled={isReadOnly}
                    />
                  </div>
                </div>
              ))}
            </div>

            {/* Add station after signing */}
            {signature.isSigned && !isReadOnly && formData.stationCount < 6 && (
              <button
                type="button"
                className="add-station-btn"
                onClick={() => handleStationCount(formData.stationCount + 1)}
              >
                + Add Another Station
              </button>
            )}
          </div>

          {/* Billing Info Section */}
          <div className="form-section">
            <h2>Billing Information</h2>
            <div className="form-grid">
              <div className="form-group">
                <label>Entity Name *</label>
                <input
                  type="text"
                  name="entityName"
                  value={formData.entityName}
                  onChange={handleChange}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label>Authorised Officer's Full Name *</label>
                <input
                  type="text"
                  name="billingContactName"
                  value={formData.billingContactName}
                  onChange={handleChange}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label>Billing Contact Email *</label>
                <input
                  type="email"
                  name="billingContactEmail"
                  value={formData.billingContactEmail}
                  onChange={handleChange}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label>Billing Contact Cell Number *</label>
                <input
                  type="tel"
                  name="billingContactPhone"
                  value={formData.billingContactPhone}
                  onChange={handleChange}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group full-width">
                <label>Billing Address - Street *</label>
                <input
                  type="text"
                  name="billingStreet"
                  value={formData.billingStreet}
                  onChange={handleChange}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label>City *</label>
                <input
                  type="text"
                  name="billingCity"
                  value={formData.billingCity}
                  onChange={handleChange}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label>State *</label>
                <input
                  type="text"
                  name="billingState"
                  value={formData.billingState}
                  onChange={handleChange}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label>Zip Code *</label>
                <input
                  type="text"
                  name="billingZip"
                  value={formData.billingZip}
                  onChange={handleChange}
                  required={!isReadOnly}
                  disabled={isReadOnly}
                />
              </div>

              <div className="form-group">
                <label>Is the Billing Contact same as the Recruiting Contact? *</label>
                <select
                  name="sameContact"
                  value={formData.sameContact}
                  onChange={handleChange}
                  required
                >
                  <option value="">Select...</option>
                  <option value="yes">Yes</option>
                  <option value="no">No</option>
                </select>
              </div>

              {formData.sameContact === 'no' && (
                <>
                  <div className="form-group">
                    <label>Recruiting Contact Name *</label>
                    <input
                      type="text"
                      name="recruitingContactName"
                      value={formData.recruitingContactName}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Recruiting Contact Phone *</label>
                    <input
                      type="tel"
                      name="recruitingContactPhone"
                      value={formData.recruitingContactPhone}
                      onChange={handleChange}
                      required
                    />
                  </div>
                </>
              )}

              <div className="form-group full-width">
                <label>Are you P&D or Linehaul? *</label>
                <div className="radio-group">
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="serviceType"
                      value="P&D"
                      checked={formData.serviceType === 'P&D'}
                      onChange={handleChange}
                      required
                      disabled={isReadOnly}
                    />
                    P&D
                  </label>
                  <label className="radio-label">
                    <input
                      type="radio"
                      name="serviceType"
                      value="Linehaul"
                      checked={formData.serviceType === 'Linehaul'}
                      onChange={handleChange}
                      required
                      disabled={isReadOnly}
                    />
                    Linehaul
                  </label>
                </div>
              </div>
            </div>
          </div>

          {/* Station Tab Bar — shown whenever there's more than 1 station */}
          {formData.stationCount > 1 && (
            <div className="station-tab-bar">
              <span className="station-tab-bar-label">Filling info for →</span>
              {Array.from({ length: formData.stationCount }).map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={`station-tab-btn${activeStation === i ? ' active' : ''}`}
                  onClick={() => setActiveStation(i)}
                >
                  Station {i + 1}
                </button>
              ))}
            </div>
          )}

          {/* CSA Info Section - P&D only */}
          {formData.serviceType !== 'Linehaul' && (() => {
            const st = formData.stations?.[activeStation] || emptyStation();
            return (
            <div className="form-section">
              <div className="section-header-with-tabs">
                <h2>CSA Information</h2>
                {formData.stationCount > 1 && <span className="section-station-badge">Station {activeStation + 1}</span>}
              </div>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Area your CSA covers (Cities or Towns)</label>
                  <small style={{ display: 'block', color: '#6b7280', marginBottom: '6px' }}>Please include all the cities that you typically hire from around you.</small>
                  <textarea name="csaCoverage" value={st.csaCoverage} onChange={handleStationChange} rows="3" disabled={isReadOnly} />
                </div>
                <div className="form-group">
                  <label>Types of Routes</label>
                  <input type="text" name="routeTypes" value={st.routeTypes} onChange={handleStationChange} disabled={isReadOnly} placeholder="Business, Residential, Rural" />
                </div>
                <div className="form-group">
                  <label>Intended Number of Monthly Hires</label>
                  <input type="number" name="monthlyHires" value={st.monthlyHires} onChange={handleStationChange} />
                </div>
                <div className="form-group full-width">
                  <label>Fleet Details</label>
                  <textarea name="fleetDetails" value={st.fleetDetails} onChange={handleStationChange} rows="3" placeholder="Please share details of your trucks" />
                </div>
                <div className="form-group full-width">
                  <label>Current Indeed Advertisement Link</label>
                  <input type="text" name="indeedLink" value={st.indeedLink} onChange={handleStationChange} />
                </div>
                <div className="form-group full-width">
                  <label>Please add this email ID as Admin to your Indeed account</label>
                  <input type="email" value="support@mountainrecruiting.com" readOnly style={{ background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' }} />
                </div>
                <div className="form-group full-width">
                  <label>3 Pre-screening Questions You usually ask the candidates?</label>
                  <textarea name="prescreeningQuestions" value={st.prescreeningQuestions} onChange={handleStationChange} rows="4" disabled={isReadOnly} />
                </div>
              </div>
            </div>
            );
          })()}

          {/* FADV Details Section */}
          {(() => {
            const st = formData.stations?.[activeStation] || emptyStation();
            return (
            <div className="form-section">
              <div className="section-header-with-tabs">
                <h2>FADV Details</h2>
                {formData.stationCount > 1 && <span className="section-station-badge">Station {activeStation + 1}</span>}
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>Client ID</label>
                  <input type="text" name="fadvClientId" value={st.fadvClientId} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>Username</label>
                  <input type="text" name="fadvUsername" value={st.fadvUsername} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>Password</label>
                  <input type="text" name="fadvPassword" value={st.fadvPassword} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>Answer to Secret Question</label>
                  <input type="text" name="fadvSecretAnswer" value={st.fadvSecretAnswer} onChange={handleStationChange} />
                </div>
              </div>
            </div>
            );
          })()}

          {/* Linehaul Driver Schedule and Pay Section */}
          {formData.serviceType === 'Linehaul' && (() => {
            const st = formData.stations?.[activeStation] || emptyStation();
            return (
            <div className="form-section">
              <div className="section-header-with-tabs">
                <h2>Driver Schedule and Pay</h2>
                {formData.stationCount > 1 && <span className="section-station-badge">Station {activeStation + 1}</span>}
              </div>
              <div className="form-grid">
                <div className="form-group full-width">
                  <label>Assigned or Unassigned?</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" name="lhAssigned" checked={st.lhAssigned} onChange={handleStationChange} disabled={isReadOnly} /> Assigned</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhUnassigned" checked={st.lhUnassigned} onChange={handleStationChange} disabled={isReadOnly} /> Unassigned</label>
                  </div>
                </div>
                {st.lhAssigned && (
                  <div className="form-group full-width">
                    <label>Assigned Lanes</label>
                    <textarea name="lhAssignedLanes" value={st.lhAssignedLanes} onChange={handleStationChange} rows="2" disabled={isReadOnly} placeholder="Describe the assigned lanes" />
                  </div>
                )}
                <div className="form-group full-width">
                  <label>Do they run Solo, as a Team, or Both?</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" name="lhSoloRun" checked={st.lhSoloRun} onChange={handleStationChange} disabled={isReadOnly} /> Solo</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhTeamRun" checked={st.lhTeamRun} onChange={handleStationChange} disabled={isReadOnly} /> Team</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhBothRun" checked={st.lhBothRun} onChange={handleStationChange} disabled={isReadOnly} /> Both</label>
                  </div>
                </div>
                <div className="form-group">
                  <label>What time do they dispatch?</label>
                  <input type="time" name="lhDispatchTime" value={st.lhDispatchTime} onChange={handleStationChange} disabled={isReadOnly} />
                </div>
                <div className="form-group full-width">
                  <label>What is the driver's weekly schedule?</label>
                  <textarea name="lhWeeklySchedule" value={st.lhWeeklySchedule} onChange={handleStationChange} rows="3" disabled={isReadOnly} />
                </div>
                <div className="form-group full-width">
                  <label>Required Endorsements?</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" name="lhEndorsementNone" checked={st.lhEndorsementNone} onChange={handleStationChange} disabled={isReadOnly} /> None</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhEndorsementDoubles" checked={st.lhEndorsementDoubles} onChange={handleStationChange} disabled={isReadOnly} /> Doubles</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhEndorsementTriples" checked={st.lhEndorsementTriples} onChange={handleStationChange} disabled={isReadOnly} /> Triples</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhEndorsementHazmats" checked={st.lhEndorsementHazmats} onChange={handleStationChange} disabled={isReadOnly} /> Hazmats</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhEndorsementOther" checked={st.lhEndorsementOther} onChange={handleStationChange} disabled={isReadOnly} /> Other</label>
                  </div>
                  {st.lhEndorsementOther && <input type="text" name="lhEndorsementOtherText" value={st.lhEndorsementOtherText} onChange={handleStationChange} disabled={isReadOnly} placeholder="Please specify endorsement" className="other-text-field" />}
                </div>
                <div className="form-group full-width">
                  <label>What type of trucks/equipment?</label>
                  <textarea name="lhTruckInfo" value={st.lhTruckInfo} onChange={handleStationChange} rows="2" disabled={isReadOnly} placeholder="e.g. 53ft dry van, flatbed, reefer" />
                </div>
                <div className="form-group full-width">
                  <label>Pay Structure</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" name="lhPayPerMile" checked={st.lhPayPerMile} onChange={handleStationChange} disabled={isReadOnly} /> Per Mile</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhFlatDailyWage" checked={st.lhFlatDailyWage} onChange={handleStationChange} disabled={isReadOnly} /> Flat Daily Wage</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhHourly" checked={st.lhHourly} onChange={handleStationChange} disabled={isReadOnly} /> Hourly</label>
                    <label className="checkbox-label"><input type="checkbox" name="lhPayOther" checked={st.lhPayOther} onChange={handleStationChange} disabled={isReadOnly} /> Other</label>
                  </div>
                  {st.lhPayOther && <input type="text" name="lhPayOtherText" value={st.lhPayOtherText} onChange={handleStationChange} disabled={isReadOnly} placeholder="Please specify pay structure" className="other-text-field" />}
                </div>
                {st.lhPayPerMile && (
                  <div className="form-group">
                    <label>Per Mile Amount</label>
                    <input type="text" name="lhPayPerMileAmount" value={st.lhPayPerMileAmount} onChange={handleStationChange} disabled={isReadOnly} placeholder="e.g. $0.55/mile" />
                  </div>
                )}
                <div className="form-group">
                  <label>Pay Period</label>
                  <select name="lhPayPeriod" value={st.lhPayPeriod} onChange={handleStationChange} disabled={isReadOnly}>
                    <option value="">Select...</option>
                    <option value="weekly">Weekly</option>
                    <option value="bi-weekly">Bi-weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Any Incentives/Bonuses?</label>
                  <textarea name="lhIncentives" value={st.lhIncentives} onChange={handleStationChange} rows="2" disabled={isReadOnly} />
                </div>
                <div className="form-group full-width">
                  <label>Any vacation/sick time in the 1st year?</label>
                  <textarea name="lhVacationSickTime" value={st.lhVacationSickTime} onChange={handleStationChange} rows="2" disabled={isReadOnly} />
                </div>
                <div className="form-group full-width">
                  <label>Do you offer any benefits?</label>
                  <textarea name="lhBenefits" value={st.lhBenefits} onChange={handleStationChange} rows="2" disabled={isReadOnly} />
                </div>
                <div className="form-group full-width">
                  <label>Any Additional Information that we haven't covered yet?</label>
                  <textarea name="lhAdditionalInfo" value={st.lhAdditionalInfo} onChange={handleStationChange} rows="4" disabled={isReadOnly} />
                </div>
              </div>
            </div>
            );
          })()}

          {/* P&D Driver Schedule and Pay Section */}
          {formData.serviceType !== 'Linehaul' && (() => {
            const st = formData.stations?.[activeStation] || emptyStation();
            return (
            <div className="form-section">
              <div className="section-header-with-tabs">
                <h2>Driver Schedule and Pay</h2>
                {formData.stationCount > 1 && <span className="section-station-badge">Station {activeStation + 1}</span>}
              </div>
              <div className="form-grid">
                <div className="form-group">
                  <label>What time does the driver report to the terminal?</label>
                  <input type="time" name="reportTime" value={st.reportTime} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>What time do they dispatch from the terminal?</label>
                  <input type="time" name="dispatchTime" value={st.dispatchTime} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>On Road Hours</label>
                  <input type="text" name="onRoadHours" value={st.onRoadHours} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>How many days do you operate?</label>
                  <select name="operatingDays" value={st.operatingDays} onChange={handleStationChange} disabled={isReadOnly}>
                    <option value="">Select...</option>
                    <option value="6">6 days</option>
                    <option value="7">7 days</option>
                  </select>
                </div>
                <div className="form-group full-width">
                  <label>Driver Schedule for New Hires? (How many days do they get off? Do they get consecutive days off early on?)</label>
                  <textarea name="newHireSchedule" value={st.newHireSchedule} onChange={handleStationChange} rows="3" disabled={isReadOnly} />
                </div>
                <div className="form-group">
                  <label>Training Pay</label>
                  <input type="text" name="trainingPay" value={st.trainingPay} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>Regular Pay</label>
                  <input type="text" name="regularPay" value={st.regularPay} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>Top Driver Pay</label>
                  <input type="text" name="topDriverPay" value={st.topDriverPay} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>How many days does training last?</label>
                  <input type="text" name="trainingDuration" value={st.trainingDuration} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>Is Overtime available?</label>
                  <input type="text" name="overtimeAvailable" value={st.overtimeAvailable} onChange={handleStationChange} />
                </div>
                <div className="form-group">
                  <label>What's the rate for Overtime?</label>
                  <input type="text" name="overtimeRate" value={st.overtimeRate} onChange={handleStationChange} />
                </div>
                <div className="form-group full-width">
                  <label>Do you offer any Bonuses?</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" name="stopBonus" checked={st.stopBonus} onChange={handleStationChange} disabled={isReadOnly} /> Stop Bonus</label>
                    <label className="checkbox-label"><input type="checkbox" name="performanceBonus" checked={st.performanceBonus} onChange={handleStationChange} disabled={isReadOnly} /> Performance</label>
                    <label className="checkbox-label"><input type="checkbox" name="safetyBonus" checked={st.safetyBonus} onChange={handleStationChange} disabled={isReadOnly} /> Safety</label>
                    <label className="checkbox-label"><input type="checkbox" name="attendanceBonus" checked={st.attendanceBonus} onChange={handleStationChange} disabled={isReadOnly} /> Attendance</label>
                  </div>
                  {st.stopBonus && <input type="text" name="stopBonusDetails" value={st.stopBonusDetails} onChange={handleStationChange} disabled={isReadOnly} placeholder="Tell us more about the stop bonus" className="other-text-field" />}
                </div>
                <div className="form-group full-width">
                  <label>Any vacation/sick time in the 1st year?</label>
                  <textarea name="vacationSickTime" value={st.vacationSickTime} onChange={handleStationChange} rows="2" disabled={isReadOnly} />
                </div>
                <div className="form-group full-width">
                  <label>Do you offer any benefits?</label>
                  <div className="checkbox-group">
                    <label className="checkbox-label"><input type="checkbox" name="ipads" checked={st.ipads} onChange={handleStationChange} disabled={isReadOnly} /> iPads</label>
                    <label className="checkbox-label"><input type="checkbox" name="medical" checked={st.medical} onChange={handleStationChange} disabled={isReadOnly} /> Medical
                  </label>
                    <label className="checkbox-label"><input type="checkbox" name="dental" checked={st.dental} onChange={handleStationChange} disabled={isReadOnly} /> Dental</label>
                    <label className="checkbox-label"><input type="checkbox" name="vision" checked={st.vision} onChange={handleStationChange} disabled={isReadOnly} /> Vision</label>
                    <label className="checkbox-label"><input type="checkbox" name="retirement401k" checked={st.retirement401k} onChange={handleStationChange} disabled={isReadOnly} /> 401K</label>
                    <label className="checkbox-label"><input type="checkbox" name="retirement401kMatching" checked={st.retirement401kMatching} onChange={handleStationChange} disabled={isReadOnly} /> 401K Matching</label>
                  </div>
                </div>
                <div className="form-group full-width">
                  {/* Field name kept as otherBenefits (not renamed) so existing saved onboarding
                      data isn't orphaned -- only the visible label changed. */}
                  <label>When do the benefits start?</label>
                  <input type="text" name="otherBenefits" value={st.otherBenefits} onChange={handleStationChange} />
                </div>
              </div>
            </div>
            );
          })()}

          {/* Admin: Signature status + clear button */}
          {user.role?.toLowerCase() === 'admin' && isReadOnly && (
            <div className="form-section" style={{ marginTop: '24px' }}>
              <h2>Signature Status</h2>
              {signature.isSigned && signature.timestamp ? (
                <div className="signature-confirmation">
                  <div className="signature-info">
                    <strong>Signed by:</strong> {signature.name}<br />
                    <strong>Company:</strong> {signature.company}<br />
                    <strong>Signed on:</strong> {new Date(signature.timestamp).toLocaleString()}
                  </div>
                  <div className="signature-status">✓ Digitally Signed</div>
                  <button
                    type="button"
                    onClick={handleClearSignature}
                    style={{
                      marginTop: '10px',
                      padding: '6px 16px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '13px'
                    }}
                  >
                    Clear Signature
                  </button>
                </div>
              ) : (
                <p style={{ color: '#888', fontStyle: 'italic' }}>Not yet signed.</p>
              )}
            </div>
          )}

          {/* Terms & Conditions and Signature Section */}
          {user.role?.toLowerCase() === 'customer' && (
            <div className="form-section terms-section">
              <h2>Terms & Conditions - Mountain Group LLC</h2>
              <div className="terms-content" style={{
                maxHeight: '300px',
                overflowY: 'auto',
                border: '1px solid #ddd',
                padding: '20px',
                backgroundColor: '#f9f9f9',
                borderRadius: '4px',
                marginBottom: '20px'
              }}>
                <p>Mountain Group LLC, a Wyoming registered limited liability company, provides recruiting services subject to the following terms and conditions:</p>

                <h3>1. Service Agreement</h3>
                <p>By engaging Mountain Group LLC's services, you agree to these terms and conditions. Services begin upon receipt of payment and execution of this agreement.</p>

                <h3>2. Payment Terms</h3>
                <p>Payment is required in advance of service delivery. Billing cycles are monthly unless otherwise agreed in writing. All fees are non-refundable once the service period has commenced.</p>

                <h3>3. Service Start</h3>
                <p>Services may begin at any time upon client request, subject to availability and receipt of advance payment for the initial billing period.</p>

                <h3>4. Pause Policy</h3>
                <p>Clients may pause services at any time by providing seven (7) days written notice. The pause will take effect at the end of the seven-day notice period. No refunds will be issued for unused days within the current paid period. Services will resume upon client request and receipt of payment for the new billing cycle.</p>

                <h3>5. Cancellation</h3>
                <p>Either party may terminate this agreement with seven (7) days written notice. No refunds will be provided for the current paid period. All outstanding fees must be paid prior to termination.</p>

                <h3>6. Confidentiality</h3>
                <p>Both parties agree to maintain confidentiality of all proprietary information shared during the engagement.</p>

                <h3>7. Limitation of Liability</h3>
                <p>Mountain Group LLC provides recruitment services on a best-effort basis but does not guarantee specific hiring outcomes or timelines. Our liability is limited to the fees paid for services rendered.</p>

                <h3>8. Governing Law</h3>
                <p>This agreement shall be governed by and construed in accordance with the laws of the State of Wyoming.</p>

                <h3>9. Changes to Terms</h3>
                <p>Mountain Group LLC reserves the right to modify these terms with thirty (30) days notice to active clients.</p>

                <p style={{ marginTop: '20px', fontWeight: 'bold' }}>By using our services, you acknowledge that you have read, understood, and agree to be bound by these terms and conditions.</p>
              </div>

              <div className="signature-block">
                <h3>Signature</h3>
                <p className="signature-instruction">By entering your name and company name below, you acknowledge that you have read, understood, and agree to be bound by these terms and conditions.</p>

                <div className="signature-fields">
                  <div className="form-group">
                    <label>Your Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={signature.name}
                      onChange={handleSignatureChange}
                      disabled={signature.isSigned || isReadOnly}
                      required
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div className="form-group">
                    <label>Company Name *</label>
                    <input
                      type="text"
                      name="company"
                      value={signature.company}
                      onChange={handleSignatureChange}
                      disabled={signature.isSigned || isReadOnly}
                      required
                      placeholder="Enter your company name"
                    />
                  </div>

                  {!signature.isSigned && !isReadOnly && (
                    <div className="form-group">
                      <button
                        type="button"
                        onClick={handleSignDocument}
                        className="sign-btn"
                        style={{
                          marginTop: '10px',
                          padding: '12px 24px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '16px',
                          fontWeight: '600'
                        }}
                      >
                        Sign Document
                      </button>
                    </div>
                  )}
                </div>

                {signature.isSigned && signature.timestamp && (
                  <div className="signature-confirmation">
                    <div className="signature-info">
                      <strong>Signed by:</strong> {signature.name}<br />
                      <strong>Company:</strong> {signature.company}<br />
                      <strong>Signed on:</strong> {new Date(signature.timestamp).toLocaleString()}
                    </div>
                    <div className="signature-status">✓ Digitally Signed</div>
                    {user.role?.toLowerCase() === 'admin' && (
                      <button
                        type="button"
                        onClick={handleClearSignature}
                        style={{
                          marginTop: '10px',
                          padding: '6px 16px',
                          backgroundColor: '#dc3545',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '13px'
                        }}
                      >
                        Clear Signature
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

          {!isReadOnly && (
            <div className="form-actions">
              <button type="submit" className="submit-btn">Save Onboarding Information</button>
              {user?.qbo_payment_link ? (
                <a
                  href={user.qbo_payment_link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="submit-btn pay-now-btn"
                  style={{ background: '#0077c5', textDecoration: 'none', display: 'inline-flex', alignItems: 'center' }}
                >
                  Pay Now
                </a>
              ) : (
                <button type="button" className="submit-btn pay-now-btn" disabled style={{ opacity: 0.4, cursor: 'not-allowed' }}>
                  Pay Now
                </button>
              )}
            </div>
          )}
        </form>
          </>
        )}
      </div>
    </div>
  );
}

export default OnboardingInformation;
